import React, { Component } from 'react';
import Router from 'next/router';
import * as Sentry from '@sentry/browser';

import loadAuth from '../apis/auth';
import loadDatabase from '../apis/database';
import loadFunction from '../apis/functions';
import { getUserData$ } from '../apis/user';
import { Subscription } from 'rxjs';
import { User } from '../interfaces/user';
import { convertParamsToObject, isThirtyDaysLockInUser } from '../helpers/general';
import { getImpersonationSession } from '../helpers/impersonation';

let database = null;
let auth = null;
let functions = null;

export const UserContext = React.createContext(null);

const defaultUserState: User = {
    loggedIn: false,
    user: null,
    createdAt: null,
    profile: {
        name: null,
        email: null,
        country: null,
        gender: null,
    },
    preferences: {},
    nutritionalInfo: {},
    planMilestones: {
        planReleased: null,
        planReviewed: null,
        questionnaireComplete: null,
        week17ModalShown: false,
        welcomeModalShown: false,
    },
    other: {},
    currentPlan: {},
    currentWeek: 0,
    plans: {},
    compliancyData: {},
    roles: {},
    isLoaded: false,
    logout: () => {},
    claims: {
        impersonator: undefined
    },
    review: undefined,
    openInNutritionAppLink: '',
    showOpenInNutritionAppBanner: false
};

export default class UserProvider extends Component<{}, User> {
    userListener?: Subscription;
    authStateListener?: () => void;

    constructor(props) {
        super(props);

        this.logout = this.logout.bind(this);
        this.getUserAdditionalData = this.getUserAdditionalData.bind(this);
        this.cleanUpListeners = this.cleanUpListeners.bind(this);
        this.handleRouteChangeComplete = this.handleRouteChangeComplete.bind(this);
        this.enforceCtaRedirect = this.enforceCtaRedirect.bind(this);
        this.userListener = null;
        this.authStateListener = null;

        this.state = {
            ...defaultUserState,
        };
    }

    async componentDidMount() {
        // The handoff page signs into its isolated session before loading user data.
        if (Router.pathname === '/lens') return;

        if (!auth) {
            auth = await loadAuth();
        }
        if (!database) {
            database = await loadDatabase();
        }

        if (!database) {
            database = await loadDatabase();
        }

        if (!functions) {
            functions = await loadFunction();
        }

        this.authStateListener = await auth.onAuthStateChanged(async user => {
            const { route } = Router;

            if (user) {
                const idTokenResult = await auth.currentUser.getIdTokenResult();
                const { uid, displayName, email } = user;
                this.setState({
                    loggedIn: true,
                    user,
                    claims: idTokenResult.claims
                });

                Sentry.configureScope(scope => {
                    scope.setUser({
                        id: uid,
                        username: displayName,
                        email,
                    });
                });

                await this.getUserAdditionalData(uid);

                if (route.includes('/login')) {
                    const { redirect_uri } = convertParamsToObject(window.location);

                    if (redirect_uri) {
                        Router.push(redirect_uri);
                    } else {
                        Router.push('/dashboard');
                    }
                }
            } else {
                this.cleanUpListeners();
                this.setState({
                    ...defaultUserState,
                });

                if (
                    !route.includes('/complete-signup') &&
                    !route.includes('/request-password-reset') &&
                    !route.includes('/login') &&
                    !route.includes('/free-nutrition-calculator')
                ) {
                    Router.push(`/login?redirect_uri=${window.location.pathname}`);
                }
            }
        });

        Router.events.on('routeChangeComplete', this.handleRouteChangeComplete);
    }

    componentWillUnmount() {
        this.cleanUpListeners();
        if (this.authStateListener) {
            this.authStateListener();
            this.authStateListener = null;
        }
        Router.events.off('routeChangeComplete', this.handleRouteChangeComplete);
    }

    async handleRouteChangeComplete(url) {
        await this.enforceCtaRedirect(url);
    }

    async enforceCtaRedirect(url?: string) {
        const userId = this.state?.user?.uid;
        if (!userId) return;

        const route = url || Router.route;
        if (route.startsWith('/30-day-lock-in-complete')) return;

        if (!database) {
            database = await loadDatabase();
        }

        const userData = await database
            .collection('users')
            .doc(userId)
            .get()
            .then((s) => s.data());

        const isThirtyDayLockInCategory =
            userData?.currentPlan?.planCategory === '30-days-lock-in';

        if (!isThirtyDayLockInCategory) return;

        const isActive = await isThirtyDaysLockInUser(userId);
        if (!isActive) {
            Router.replace('/30-day-lock-in-complete');
        }
    }

    async getUserAdditionalData(userId) {
        if (this.userListener) {
            return;
        }

        this.userListener = getUserData$(userId).subscribe(async userData => {
            const { currentPlan, elevatePlan } = userData;
            
            if (currentPlan) {
                await this.enforceCtaRedirect();

                const dynamicLinkParams = await database.collection('deeplink').doc('data').get();
                const nutritionAppLink = dynamicLinkParams?.data()?.openInAppLink;
                const showOpenInNutritionAppBanner =
                    !!nutritionAppLink && currentPlan.productType === 'nutrition';
                if (
                    !this.state.isLoaded &&
                    !this.state.claims?.impersonator &&
                    !getImpersonationSession()
                ) {
                    database
                        .collection('users')
                        .doc(userId)
                        .set({ other: { lastLoggedIn: new Date() } }, { merge: true });

                    functions.httpsCallable('logSession')({}).catch(error => {
                        console.log(error);
                    });
                }
                this.setState({
                    ...userData,
                    openInNutritionAppLink: nutritionAppLink,
                    showOpenInNutritionAppBanner,
                    isLoaded: true
                });
            } else if(elevatePlan && !currentPlan) {
                this.cleanUpListeners();
                await auth.signOut();
                Router.push('/login?elevateUser=true');
            } else {
                this.cleanUpListeners();
                await auth.signOut();
                Router.push('/login?accountExpired=true');
            }
        });
    }

    cleanUpListeners() {
        if (this.userListener) {
            this.userListener.unsubscribe();
            this.userListener = null;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    async logout() {
        try {
            this.cleanUpListeners();
            await auth.signOut();
            Router.push('/login');
        } catch (error) {
            console.log('Error when trying to logout', error);
        }
    }

    hideOpenInNutritionAppBanner = () => {
        this.setState({
            showOpenInNutritionAppBanner: false,
        });
    };

    render() {
        const { children } = this.props;

        return (
            <UserContext.Provider
                value={{
                    ...this.state,
                    logout: this.logout,
                    hideOpenInNutritionAppBanner: this.hideOpenInNutritionAppBanner,
                }}
            >
                {children}
            </UserContext.Provider>
        );
    }
}
